import React from 'react';
import PropTypes from 'prop-types';
import { formatNumber } from 'helper';
import ReactApexCharts from 'react-apexcharts';

export function HorizontalBarChart({
  data,
  valueKey,
  horizontal,
  isLoading,
  topPadding,
  bottomPadding,
  leftPadding,
  rightPadding,
  ...rest
}) {
  const final = (data || [])?.filter((x) => x[valueKey] > 1);
  const options = {
    tooltip: {
      y: {
        formatter: (_, series) =>
          formatNumber(final?.[series.dataPointIndex]?.count, 0),
      },
    },
    stroke: {
      colors: ['transparent'],
      width: 5,
    },
    grid: {
      show: false,
      padding: {
        top: topPadding,
        right: rightPadding,
        bottom: bottomPadding,
        left: leftPadding,
      },
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'left',
      offsetX: 20,
      offsetY: 5,
      itemMargin: {
        horizontal: 0,
        vertical: 2,
      },
    },
    yaxis: {
      labels: {
        show: false,
      },
    },
    xaxis: {
      categories: final.map((item, i) => item?.title || `Data ${i + 1}`),
      labels: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal,
        distributed: true,
        barHeight: '100%',
        dataLabels: {
          position: 'top',
          formatter: (val) => [`${formatNumber(val, 0)}`],
        },
      },
    },
    dataLabels: {
      formatter: (val) => [`${formatNumber(val, 0)}`],
      enabled: true,
      offsetX: 60,
      offsetY: 0,
      textAnchor: 'middle',
      style: {
        fontSize: '14px',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontWeight: 'bold',
        colors: [
          '#A5A5A5',
          '#003554',
          '#003554',
          '#003554',
          '#003554',
          '#003554',
          '#003554',
          '#003554',
          '#003554',
          '#003554',
          '#003554',
        ],
      },
    },
    chart: {
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: [
      '#003554',
      '#006494',
      '#0582CA',
      '#00B4CC',
      '#F88125',
      '#FAAB6D',
      '#FBD016',
      '#FED766',
      '#FE4A49',
    ],
    ...rest,
  };

  const series = [
    {
      name: 'Total',
      data: final?.map((item) => item[valueKey]?.toFixed(0) ?? null),
    },
  ];

  if (isLoading) {
    if (!horizontal) {
      return (
        <div className="h-full w-full flex items-end space-x-3 p-4">
          <div className="animate-pulse bg-gray-50 h-full w-full rounded" />
          <div className="animate-pulse bg-gray-50 h-5/6 w-full rounded" />
          <div className="animate-pulse bg-gray-50 h-3/6 w-full rounded" />
          <div className="animate-pulse bg-gray-50 h-2/5 w-full rounded" />
          <div className="animate-pulse bg-gray-50 h-1/6 w-full rounded" />
        </div>
      );
    }
    return (
      <div className="space-y-3 p-4">
        <div className="animate-pulse bg-gray-50 h-10 w-full rounded" />
        <div className="animate-pulse bg-gray-50 h-10 w-5/6 rounded" />
        <div className="animate-pulse bg-gray-50 h-10 w-3/6 rounded" />
        <div className="animate-pulse bg-gray-50 h-10 w-2/5 rounded" />
        <div className="animate-pulse bg-gray-50 h-10 w-1/6 rounded" />
      </div>
    );
  }
  return (
    <ReactApexCharts
      height="100%"
      width="100%"
      options={options}
      series={series}
      type="bar"
    />
  );
}

HorizontalBarChart.defaultProps = {
  valueKey: 'count',
  data: {
    labels: [],
    series: [],
  },
  horizontal: true,
  isLoading: false,
  topPadding: 0,
  leftPadding: 10,
  bottomPadding: 10,
  rightPadding: 20,
};

HorizontalBarChart.propTypes = {
  data: PropTypes.instanceOf(Object),
  valueKey: PropTypes.string,
  horizontal: PropTypes.bool,
  isLoading: PropTypes.bool,
  topPadding: PropTypes.number,
  leftPadding: PropTypes.number,
  bottomPadding: PropTypes.number,
  rightPadding: PropTypes.number,
};

export function BarChart({ data, valueKey, horizontal, isLoading }) {
  const options = {
    stroke: {
      curve: 'smooth',
    },
    xaxis: {
      categories: (data || []).map((item, i) => item?.title || `Data ${i + 1}`),
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal,
      },
    },
    dataLabels: {
      formatter(val) {
        return [`${formatNumber(val, 0)}`];
      },
    },
    chart: {
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: ['#2486bc'],
  };

  const series = [
    {
      data: (data || []).map((item) => item[valueKey] ?? null),
    },
  ];
  if (isLoading) {
    if (!horizontal) {
      return (
        <div className="h-full w-full flex items-end space-x-3 p-4">
          <div className="animate-pulse bg-gray-50 h-full w-full rounded" />
          <div className="animate-pulse bg-gray-50 h-5/6 w-full rounded" />
          <div className="animate-pulse bg-gray-50 h-3/6 w-full rounded" />
          <div className="animate-pulse bg-gray-50 h-2/5 w-full rounded" />
          <div className="animate-pulse bg-gray-50 h-1/6 w-full rounded" />
        </div>
      );
    }
    return (
      <div className="space-y-3 p-4">
        <div className="animate-pulse bg-gray-50 h-10 w-full rounded" />
        <div className="animate-pulse bg-gray-50 h-10 w-5/6 rounded" />
        <div className="animate-pulse bg-gray-50 h-10 w-3/6 rounded" />
        <div className="animate-pulse bg-gray-50 h-10 w-2/5 rounded" />
        <div className="animate-pulse bg-gray-50 h-10 w-1/6 rounded" />
      </div>
    );
  }
  return (
    <ReactApexCharts
      height="100%"
      options={options}
      series={series}
      type="bar"
    />
  );
}

BarChart.defaultProps = {
  valueKey: 'count',
  data: {
    labels: [],
    series: [],
  },
  horizontal: true,
  isLoading: false,
};

BarChart.propTypes = {
  data: PropTypes.instanceOf(Object),
  valueKey: PropTypes.string,
  horizontal: PropTypes.bool,
  isLoading: PropTypes.bool,
};
